/**
 * Visual bar showing where rho falls inside the admissible range,
 * with the three normative markers: 1% (E.060 min), 2.5% (ACI 318 max for
 * earthquake-resistant frames, recommended), and 6% (E.060 absolute max).
 */

interface Props {
  rho: number; // dimensionless ratio (e.g. 0.01 = 1%)
}

export function CuantiaBar({ rho }: Props) {
  const max = 0.07; // bar goes from 0% to 7%
  const pct = Math.max(0, Math.min(rho / max, 1)) * 100;

  const inRange = rho >= 0.01 && rho <= 0.06;
  const aboveAci = rho > 0.025;
  const tone = !inRange ? 'var(--danger)' : aboveAci ? 'var(--warning)' : 'var(--success)';

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between text-[10px]" style={{ color: 'var(--text-muted)' }}>
        <span>0%</span>
        <span style={{ color: tone, fontFamily: 'var(--font-mono)' }}>
          ρ = {(rho * 100).toFixed(2)} %
        </span>
        <span>7%</span>
      </div>

      <div
        className="relative h-2.5 w-full overflow-hidden rounded-[var(--radius-pill)]"
        style={{ background: 'var(--surface-3)' }}
      >
        {/* Admissible band 1% - 6% */}
        <span
          className="absolute top-0 h-full"
          style={{
            left: `${(0.01 / max) * 100}%`,
            width: `${((0.06 - 0.01) / max) * 100}%`,
            background: 'rgba(34,197,94,0.20)',
          }}
        />
        {/* ACI recommended band 1% - 2.5% (slightly stronger green) */}
        <span
          className="absolute top-0 h-full"
          style={{
            left: `${(0.01 / max) * 100}%`,
            width: `${((0.025 - 0.01) / max) * 100}%`,
            background: 'rgba(34,197,94,0.35)',
          }}
        />

        {/* Markers */}
        {[
          { x: 0.01, label: '1%', color: 'var(--success)' },
          { x: 0.025, label: '2.5%', color: 'var(--warning)' },
          { x: 0.06, label: '6%', color: 'var(--danger)' },
        ].map((m) => (
          <span
            key={m.label}
            className="absolute top-0 h-full w-px"
            style={{ left: `${(m.x / max) * 100}%`, background: m.color }}
          />
        ))}

        {/* Cursor for current rho */}
        <span
          className="absolute -top-0.5 h-3.5 w-1 rounded-full transition-all duration-200"
          style={{
            left: `calc(${pct}% - 2px)`,
            background: tone,
            boxShadow: `0 0 0 2px var(--surface-1), 0 0 8px ${tone}`,
          }}
        />
      </div>

      <div className="flex items-center justify-between text-[9px] font-mono tabular" style={{ color: 'var(--text-muted)' }}>
        <span style={{ color: 'var(--success)' }}>1% min E.060</span>
        <span style={{ color: 'var(--warning)' }}>2.5% rec.</span>
        <span style={{ color: 'var(--danger)' }}>6% max E.060</span>
      </div>

      <div className="text-[10.5px]" style={{ color: 'var(--text-secondary)', lineHeight: 1.45 }}>
        {!inRange && rho < 0.01 && (
          <>
            <span style={{ color: 'var(--danger)', fontWeight: 600 }}>Bajo el minimo. </span>
            La seccion esta sobredimensionada en concreto. Sube cantidad o diametro de barras, o reduce L1 / L2.
          </>
        )}
        {!inRange && rho > 0.06 && (
          <>
            <span style={{ color: 'var(--danger)', fontWeight: 600 }}>Excede el maximo. </span>
            Hay congestion de barras. Agranda la seccion o reduce el armado.
          </>
        )}
        {inRange && aboveAci && (
          <>
            <span style={{ color: 'var(--warning)', fontWeight: 600 }}>Sobre 2.5%. </span>
            Cumple E.060 pero supera lo recomendado por ACI para porticos sismorresistentes.
          </>
        )}
        {inRange && !aboveAci && (
          <>
            <span style={{ color: 'var(--success)', fontWeight: 600 }}>Optimo. </span>
            Dentro del rango sismorresistente recomendado (1% - 2.5%).
          </>
        )}
      </div>
    </div>
  );
}
